import { PossibleBigNumber } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isEmpty, isNil } from 'lodash-es';
import { TFunction } from 'next-i18next';
import { Nullable } from 'vitest';

import { ErrorMsg } from '../components/ErrorMsg';
import { TpSlValueState } from '../types';

export type SlValidationResult = {
  valid: boolean;
  error?: StopLossPriceError;
};

export type StopLossPriceError =
  | 'should_be_above_entry_price'
  | 'should_be_below_entry_price'
  | 'should_be_above_0'
  | 'should_be_below_liq_price'
  | 'should_be_above_liq_price'
  | 'should_be_below_market_price'
  | 'should_be_above_market_price';
export const validateStopLossPrice = ({
  isLong,
  entryPrice,
  sl,
  liqPrice,
  marketPrice,
  isMarket = true,
}: {
  entryPrice: Nullable<number>;
  isLong: boolean;
  sl: TpSlValueState | null;
  liqPrice: PossibleBigNumber;
  marketPrice: Nullable<number>;
  isMarket?: boolean;
}): SlValidationResult => {
  if (
    isNil(sl) ||
    isEmpty(sl.priceValue) ||
    sl.priceValue == '0' ||
    isNil(entryPrice) ||
    isNil(liqPrice) ||
    isNil(marketPrice)
  ) {
    return {
      valid: true,
    };
  }

  // Data is old
  if (!isNil(sl.isLong) && sl.isLong !== isLong) {
    return {
      valid: true,
    };
  }

  if (Number(sl.priceValue) <= 0) {
    return {
      valid: false,
      error: 'should_be_above_0',
    };
  }

  if (isLong) {
    if (Number(sl.priceValue) >= entryPrice) {
      return {
        valid: false,
        error: 'should_be_below_entry_price',
      };
    }
    if (Number(sl.priceValue) >= marketPrice && isMarket) {
      // only check when is market order
      return {
        valid: false,
        error: 'should_be_below_market_price',
      };
    }
    // for long, stopLossPrice should be above liqPrice
    if (liqPrice.isGreaterThanOrEqualTo(Number(sl.priceValue))) {
      return {
        valid: false,
        error: 'should_be_above_liq_price',
      };
    }
  } else {
    if (Number(sl.priceValue) <= entryPrice) {
      return {
        valid: false,
        error: 'should_be_above_entry_price',
      };
    }
    if (Number(sl.priceValue) <= marketPrice && isMarket) {
      // only check when is market order
      return {
        valid: false,
        error: 'should_be_above_market_price',
      };
    }
    // for short, stopLossPrice should be below liqPrice
    if (liqPrice.isLessThanOrEqualTo(Number(sl.priceValue))) {
      return {
        valid: false,
        error: 'should_be_below_liq_price',
      };
    }
  }

  return {
    valid: true,
  };
};

export const getStopLossPriceErrorMessage = (
  t: TFunction,
  error?: StopLossPriceError,
) => {
  if (!error) {
    return undefined;
  }

  switch (error) {
    case 'should_be_above_market_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Stop Loss Price should be above market price')}
        />
      );
    case 'should_be_below_market_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Stop Loss Price should be below market price')}
        />
      );
    case 'should_be_above_entry_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Stop Loss Price should be above entry price')}
        />
      );
    case 'should_be_below_entry_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Stop Loss Price should be below entry price')}
        />
      );
    case 'should_be_above_0':
      return (
        <ErrorMsg errorMsg={t('common.Stop Loss Price should be above 0')} />
      );
    case 'should_be_below_liq_price':
      return (
        <ErrorMsg
          errorMsg={t(
            'common.Stop Loss Price should be below liquidation price',
          )}
        />
      );
    case 'should_be_above_liq_price':
      return (
        <ErrorMsg
          errorMsg={t(
            'common.Stop Loss Price should be above liquidation price',
          )}
        />
      );
  }
};

export const isSlInvalid = (
  liqPrice: BigNumber,
  slPriceValue: string,
  isLong: boolean,
  entryPrice: BigNumber,
) => {
  const slPriceBn = new BigNumber(slPriceValue);

  if (isLong) {
    if (
      liqPrice.isGreaterThanOrEqualTo(slPriceBn) ||
      slPriceBn.isGreaterThanOrEqualTo(entryPrice)
    ) {
      return true;
    }
  } else {
    if (
      liqPrice.isLessThanOrEqualTo(slPriceBn) ||
      slPriceBn.isLessThanOrEqualTo(entryPrice)
    ) {
      return true;
    }
  }

  return false;
};
